import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/fleet`;

const AuthImg = ({ url, headers, onOpen }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let u;
    axios.get(url, { headers, responseType: 'blob' }).then((r) => { u = URL.createObjectURL(r.data); setSrc(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  return src ? <img src={src} alt="fault" onClick={() => onOpen(src)} className="h-10 w-10 object-cover rounded border cursor-zoom-in" /> : <span className="h-10 w-10 rounded border bg-slate-100 inline-block" />;
};

export const FaultPhotoThumbs = ({ faults, headers }) => {
  const [big, setBig] = useState(null);
  const photos = (faults || []).flatMap((f) => (f.photos || []).map((p) => ({ f, p })));
  if (!photos.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid="fault-photo-thumbs">
      {photos.map(({ f, p }) => <AuthImg key={p.id} url={`${API}/faults/${f.id}/photos/${p.id}`} headers={headers} onOpen={setBig} />)}
      <Dialog open={!!big} onOpenChange={(v) => !v && setBig(null)}>
        <DialogContent className="max-w-3xl"><DialogHeader><DialogTitle>Fault photo</DialogTitle><DialogDescription>Photo attached by the driver.</DialogDescription></DialogHeader>
          {big && <img src={big} alt="fault" className="w-full rounded" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const AlertSettingsCard = ({ headers }) => {
  const [s, setS] = useState(null);
  const [text, setText] = useState('');
  useEffect(() => {
    axios.get(`${API}/alert-settings`, { headers }).then((r) => { setS(r.data); setText((r.data.recipients || []).join(', ')); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const save = async (enabled = s?.enabled ?? true) => {
    try {
      const r = await axios.put(`${API}/alert-settings`, { recipients: text.split(/[,;\s]+/).filter(Boolean), enabled }, { headers });
      setS(r.data); setText(r.data.recipients.join(', ')); toast.success('Alert settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const test = async () => {
    try { await axios.post(`${API}/alert-settings/test`, {}, { headers }); toast.success('Summary email sent'); } catch { toast.error('Send failed'); }
  };
  if (!s) return null;
  return (
    <div className="rounded border bg-white p-3 flex flex-wrap items-center gap-2 text-sm" data-testid="fleet-alert-settings">
      <Mail className="h-4 w-4 text-blue-600" />
      <span className="font-semibold">Service alerts to:</span>
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="manager@email.com, other@email.com" className="w-80 h-8" data-testid="fleet-alert-recipients" />
      <Button size="sm" onClick={() => save()} data-testid="fleet-alert-save">Save</Button>
      <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={s.enabled} onChange={(e) => save(e.target.checked)} data-testid="fleet-alert-enabled" />On</label>
      <Button size="sm" variant="outline" onClick={test} data-testid="fleet-alert-test">Send summary now</Button>
      <span className="text-xs text-slate-500">Emails when a vehicle comes within 1,000 km, when it goes overdue, and a 7am daily summary.</span>
    </div>
  );
};

const MismatchRow = ({ m, headers, onDone }) => {
  const [km, setKm] = useState(String(m.previous_km));
  const [note, setNote] = useState('');
  const act = async (action) => {
    try { await axios.post(`${API}/odometer-mismatches/${m.id}/resolve`, { action, corrected_km: km, note }, { headers }); toast.success(action === 'correct' ? 'Reading corrected' : 'Accepted'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const open = m.status === 'open';
  return (
    <tr className="border-t align-top" data-testid={`mismatch-row-${m.id}`}>
      <td className="p-2 whitespace-nowrap">{m.shift_date}</td>
      <td className="p-2 font-mono">{m.registration}</td>
      <td className="p-2">{m.driver_name}</td>
      <td className="p-2 text-right font-mono">{m.previous_km.toLocaleString('en-ZA')}<div className="text-[10px] text-slate-500">{m.previous_source}</div></td>
      <td className="p-2 text-right font-mono">{m.start_km.toLocaleString('en-ZA')}</td>
      <td className={`p-2 text-right font-mono font-bold ${m.difference_km < 0 ? 'text-rose-600' : 'text-amber-600'}`}>{m.difference_km > 0 ? '+' : ''}{m.difference_km.toLocaleString('en-ZA')}</td>
      <td className="p-2">
        {open ? (
          <div className="flex flex-wrap gap-1 items-center">
            <Input type="number" value={km} onChange={(e) => setKm(e.target.value)} className="h-8 w-28" data-testid={`mismatch-km-${m.id}`} />
            <Button size="sm" className="h-8 bg-blue-600" onClick={() => act('correct')} data-testid={`mismatch-correct-${m.id}`}>Correct start km</Button>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className="h-8 w-40" data-testid={`mismatch-note-${m.id}`} />
            <Button size="sm" variant="outline" className="h-8" onClick={() => act('accept')} data-testid={`mismatch-accept-${m.id}`}>Accept</Button>
          </div>
        ) : (
          <span className="text-xs"><Badge className={m.status === 'corrected' ? 'bg-blue-600' : 'bg-emerald-600'}>{m.status}</Badge> {m.corrected_km ? `→ ${m.corrected_km.toLocaleString('en-ZA')} km` : ''} {m.note} <span className="text-slate-400">({m.resolved_by})</span></span>
        )}
      </td>
    </tr>
  );
};

export const OdometerMismatches = ({ headers, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('open');
  const load = useCallback(() => axios.get(`${API}/odometer-mismatches`, { headers, params: { status } }).then((r) => setRows(r.data)).catch(() => {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-2" data-testid="odometer-mismatches">
      <div className="flex gap-2 text-sm">
        {['open', 'corrected', 'accepted', 'all'].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-2 py-1 rounded border capitalize ${status === s ? 'bg-slate-900 text-white' : ''}`} data-testid={`mismatch-filter-${s}`}>{s}</button>
        ))}
        <span className="text-xs text-slate-500 self-center">Clock-in readings more than 5 km away from the previous clock-out. Drivers are never blocked.</span>
      </div>
      <div className="overflow-x-auto border rounded bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Vehicle</th><th className="p-2 text-left">Driver</th><th className="p-2 text-right">Previous km</th><th className="p-2 text-right">Clock-in km</th><th className="p-2 text-right">Difference</th><th className="p-2 text-left">Fix</th></tr></thead>
          <tbody>
            {rows.map((m) => <MismatchRow key={m.id} m={m} headers={headers} onDone={() => { load(); onChanged?.(); }} />)}
            {rows.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-slate-500">No {status === 'all' ? '' : status} mismatches.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
